'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import styles from './Card.module.css';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Visual variant
   * - default: Standard card with subtle border and shadow
   * - elevated: More prominent shadow (shadow-2)
   * - outlined: Border only, no shadow
   * - flat: No border or shadow
   */
  variant?: 'default' | 'elevated' | 'outlined' | 'flat';

  /**
   * Padding size
   * - none: No padding
   * - sm: 16px
   * - md: 24px (default - law for Float Card)
   * - lg: 32px
   */
  padding?: 'none' | 'sm' | 'md' | 'lg';

  /**
   * Make the card clickable with hover effects
   */
  interactive?: boolean;

  /**
   * Additional class names
   */
  className?: string;

  /**
   * Render as a different element
   */
  as?: React.ElementType;
}

/**
 * Card Component (Float Card)
 *
 * Laws:
 * - Radius: always 16px (radius-lg)
 * - Default padding: 24px
 * - Border: 1px subtle OR none + shadow-1 (we use both for depth)
 * - No heavy shadows on ordinary cards
 * - Clickable cards lift on hover (shadow-2)
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      interactive = false,
      className,
      as: Component = 'div',
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const isClickable = interactive || Boolean(onClick);

    return (
      <Component
        ref={ref}
        className={clsx(
          styles.card,
          styles[variant],
          styles[`padding-${padding}`],
          isClickable && styles.interactive,
          className
        )}
        onClick={onClick}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={
          isClickable
            ? (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
                }
              }
            : undefined
        }
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Card.displayName = 'Card';

/* ===========================================
   SUB-COMPONENTS
   =========================================== */

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.header, className)} {...props}>
      {children}
    </div>
  )
);

CardHeader.displayName = 'CardHeader';

export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Component = 'h3', children, ...props }, ref) => (
    <Component ref={ref} className={clsx(styles.title, className)} {...props}>
      {children}
    </Component>
  )
);

CardTitle.displayName = 'CardTitle';

export interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  className?: string;
}

export const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, children, ...props }, ref) => (
    <p ref={ref} className={clsx(styles.description, className)} {...props}>
      {children}
    </p>
  )
);

CardDescription.displayName = 'CardDescription';

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.content, className)} {...props}>
      {children}
    </div>
  )
);

CardContent.displayName = 'CardContent';

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.footer, className)} {...props}>
      {children}
    </div>
  )
);

CardFooter.displayName = 'CardFooter';

export default Card;
